import React, { useContext, useEffect, useState } from 'react';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Drawer, Popconfirm, Space, Spin } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import AfterHoursModal from '@totem/components/portfolio/buildings/hvac/afterHours/AfterHoursModal';
import {
  AfterHours,
  MONTH_NAMES,
} from '@totem/components/portfolio/buildings/hvac/afterHours/types';
import { HVACSpace } from '@totem/components/portfolio/buildings/hvac/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const formatCurrency = (val: number) =>
  val.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

type Props = {
  open: boolean;
  buildingId: string;
  space: HVACSpace;
  onClose: () => void;
};

const AfterHoursDrawer = ({ open, buildingId, space, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [records, setRecords] = useState<AfterHours[]>([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<AfterHours>(null);

  useEffect(() => {
    if (open && space) {
      setRefresh(true);
    }
  }, [open, space]);

  useEffect(() => {
    if (!refresh || !space) {
      return;
    }

    setRefresh(false);

    setLoading(true);

    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/hvac-spaces/${space.id}/after-hours`,
      {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => res.json())
      .then((result: AfterHours[]) => {
        setRecords(Array.isArray(result) ? result : []);
      })
      .finally(() => setLoading(false));
  }, [refresh, buildingId, space, getApiToken]);

  const handleDelete = (record: AfterHours) => {
    fetch(
      `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/after-hours/${record.id}`,
      {
        method: 'DELETE',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    ).then((res) => {
      CheckResponseShowError(res);

      setRefresh(true);
    });
  };

  const columns: ColumnProps<AfterHours>[] = [
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
      width: 70,
    },
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      width: 110,
      render: (_, record) => MONTH_NAMES[record.month - 1],
    },
    {
      title: 'Hrs Requested',
      dataIndex: 'hoursRequested',
      key: 'hoursRequested',
      align: 'right' as const,
      render: (_, record) => record.hoursRequested.toLocaleString(),
    },
    {
      title: 'Hrs Billed',
      dataIndex: 'hoursBilled',
      key: 'hoursBilled',
      align: 'right' as const,
      render: (_, record) => record.hoursBilled.toLocaleString(),
    },
    {
      title: 'Rate ($/hr)',
      dataIndex: 'billingRate',
      key: 'billingRate',
      align: 'right' as const,
      render: (_, record) =>
        record.billingRate > 0 ? `$${record.billingRate.toFixed(2)}` : '—',
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right' as const,
      render: (_, record) => formatCurrency(record.revenue),
    },
    {
      title: '',
      key: 'actions',
      align: 'right' as const,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditRecord(record);

              setModalOpen(true);
            }}
          />
          <Popconfirm
            title="Delete this record?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okType="danger"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={`After-Hours — ${space?.name ?? ''}`}
        width={760}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditRecord(null);

              setModalOpen(true);
            }}
          >
            Add Record
          </Button>
        }
      >
        <Spin spinning={loading}>
          <IBTable
            columns={columns}
            dataSource={records}
            rowKey={(record) => record.id}
            pagination={{ showSizeChanger: true, pageSize: 24 }}
            size="small"
          />
        </Spin>
      </Drawer>

      <AfterHoursModal
        open={modalOpen}
        buildingId={buildingId}
        spaceId={space?.id ?? ''}
        record={editRecord}
        onClose={() => {
          setModalOpen(false);

          setEditRecord(null);
        }}
        onSaved={() => setRefresh(true)}
      />
    </>
  );
};

export default AfterHoursDrawer;
